import type { Categories } from './Category';
import type { ProductTechnology, ProductType } from './Product';

export type Translations = Record<string, string>;

export type WidgetConfig = {
  translations?: Translations;
  options?: Record<string, any>;
};

export type ProductTypeOverrides = WidgetConfig & {
  technology?: Partial<Record<ProductTechnology, WidgetConfig>>;
};

export type WidgetConfigDefinition = WidgetConfig & {
  overrides?: {
    technologies?: Partial<Record<ProductTechnology, WidgetConfig>>;
    productTypes?: Partial<Record<ProductType, ProductTypeOverrides>>;
    categories?: Record<string, WidgetConfig>;
    products?: Record<string, WidgetConfig>;
  };
};

export function resolveWidgetConfig(
  config: WidgetConfigDefinition,
  technology: ProductTechnology,
  productType: ProductType,
  categories?: Categories,
  productId?: string
): WidgetConfig {
  const ptOverride: ProductTypeOverrides | undefined =
    config.overrides?.productTypes?.[productType];

  const ptTechOverride: WidgetConfig | undefined =
    ptOverride?.technology?.[technology];

  const matchingCategoryOverrides: WidgetConfig[] = categories
    ? Object.entries(config.overrides?.categories ?? {})
        .filter(([cat]) => categories?.has(cat))
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB, 'en-GB')) // Apply in alphabetic order
        .map(([, cfg]) => cfg)
    : [];

  return {
    translations: {
      ...config.translations,
      ...config.overrides?.technologies?.[technology]?.translations,
      ...ptOverride?.translations,
      ...ptTechOverride?.translations,
      ...Object.assign(
        {},
        ...matchingCategoryOverrides.map((cfg) => cfg.translations)
      ),
      ...(productId
        ? config.overrides?.products?.[productId]?.translations
        : {})
    },
    options: {
      ...config.options,
      ...config.overrides?.technologies?.[technology]?.options,
      ...ptOverride?.options,
      ...ptTechOverride?.options,
      ...Object.assign(
        {},
        ...matchingCategoryOverrides.map((cfg) => cfg.options)
      ),
      ...(productId ? config.overrides?.products?.[productId]?.options : {})
    }
  };
}
